import React from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import type { InputTextSectionProps } from './index';

// Field configuration for double mode
export interface InputTextFieldConfig {
  fieldId: string;
  titleRu?: string;
  titleEn?: string;
  placeholderRu?: string;
  placeholderEn?: string;
  charLimit?: number;
  required?: boolean;
}

export interface InputTextWidgetConfig {
  layout?: 'simple' | 'double';
  // Simple mode
  titleRu?: string;
  titleEn?: string;
  placeholderRu?: string;
  placeholderEn?: string;
  outputKey?: string;
  charLimit?: number;          // Default: 5000
  required?: boolean;          // Default: true
  // Double mode
  field1?: InputTextFieldConfig;
  field2?: InputTextFieldConfig;
}

export default function InputTextSection({
  workflowWidgets,
  onWidgetConfigChange,
  language
}: InputTextSectionProps) {
  const t = useLocalization(language);

  // Find the Input Text widget
  const inputTextWidget = workflowWidgets.find(
    w => w.widget_type?.name === 'Input Text' && w.is_enabled
  );

  const hasInputTextWidget = !!inputTextWidget;

  // Parse current config
  let config: InputTextWidgetConfig = {};
  if (inputTextWidget?.config_data) {
    try {
      config = JSON.parse(inputTextWidget.config_data);
    } catch (e) {
      console.warn('Failed to parse Input Text config:', e);
    }
  }

  const layout = config.layout || 'simple';

  const updateConfig = (updates: Partial<InputTextWidgetConfig>) => {
    if (!inputTextWidget) return;

    const newConfig: InputTextWidgetConfig = {
      ...config,
      ...updates
    };

    // Clean up undefined/empty values for simple mode fields
    if (newConfig.layout === 'simple' || !newConfig.layout) {
      ['titleRu', 'titleEn', 'placeholderRu', 'placeholderEn', 'outputKey'].forEach(key => {
        if (newConfig[key as keyof InputTextWidgetConfig] === undefined ||
            newConfig[key as keyof InputTextWidgetConfig] === '') {
          delete newConfig[key as keyof InputTextWidgetConfig];
        }
      });
    }

    onWidgetConfigChange(inputTextWidget.widget_type_id, JSON.stringify(newConfig));
  };

  const updateFieldConfig = (fieldKey: 'field1' | 'field2', updates: Partial<InputTextFieldConfig>) => {
    const currentField = config[fieldKey] || { fieldId: '' };
    const newField = { ...currentField, ...updates };

    // Clean up empty values
    Object.keys(newField).forEach(key => {
      if (newField[key as keyof InputTextFieldConfig] === '' ||
          newField[key as keyof InputTextFieldConfig] === undefined) {
        if (key !== 'fieldId' && key !== 'required') {
          delete newField[key as keyof InputTextFieldConfig];
        }
      }
    });

    updateConfig({ [fieldKey]: newField });
  };

  // Render field configuration section
  const renderFieldConfig = (
    fieldKey: 'field1' | 'field2',
    defaultTitle: string,
    colorClass: string
  ) => {
    const field = config[fieldKey] || { fieldId: '' };

    return (
      <section className={`bg-gradient-to-br ${colorClass} border border-${colorClass.includes('cyan') ? 'cyan' : 'purple'}-500/30 rounded-xl p-4`}>
        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <Icon name={ICONS.EDIT} size={18} className={colorClass.includes('cyan') ? 'text-cyan-400' : 'text-purple-400'} />
          {defaultTitle}
        </h4>

        {/* Field ID */}
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
            <Icon name={ICONS.TAG} size={14} className="text-gray-500" />
            {t.admin?.inputTextFieldId || 'Field ID (API Key)'}
          </label>
          <input
            type="text"
            value={field.fieldId || ''}
            onChange={(e) => updateFieldConfig(fieldKey, { fieldId: e.target.value })}
            placeholder="e.g. prompt_lyrics"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500 font-mono"
          />
          <p className="text-xs text-gray-500 mt-1">
            {t.admin?.inputTextFieldIdDesc || 'This key will be used in the API payload'}
          </p>
        </div>

        {/* Title fields */}
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-1.5">
            {t.admin?.inputTextTitle || 'Field Title'}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span className="text-base">🇷🇺</span> Russian
              </label>
              <input
                type="text"
                value={field.titleRu || ''}
                onChange={(e) => updateFieldConfig(fieldKey, { titleRu: e.target.value })}
                placeholder={language === 'ru' ? 'Заголовок поля' : 'Field title'}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span className="text-base">🇬🇧</span> English
              </label>
              <input
                type="text"
                value={field.titleEn || ''}
                onChange={(e) => updateFieldConfig(fieldKey, { titleEn: e.target.value })}
                placeholder="Field title"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Placeholder fields */}
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-1.5">
            {t.admin?.inputTextPlaceholder || 'Placeholder Text'}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span className="text-base">🇷🇺</span> Russian
              </label>
              <input
                type="text"
                value={field.placeholderRu || ''}
                onChange={(e) => updateFieldConfig(fieldKey, { placeholderRu: e.target.value })}
                placeholder={language === 'ru' ? 'Введите текст...' : 'Enter text...'}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span className="text-base">🇬🇧</span> English
              </label>
              <input
                type="text"
                value={field.placeholderEn || ''}
                onChange={(e) => updateFieldConfig(fieldKey, { placeholderEn: e.target.value })}
                placeholder="Enter text..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Character limit and required */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
              <Icon name={ICONS.SLIDERS} size={14} className="text-gray-500" />
              {t.admin?.inputTextCharLimit || 'Character Limit'}
            </label>
            <input
              type="number"
              value={field.charLimit || 5000}
              onChange={(e) => updateFieldConfig(fieldKey, { charLimit: parseInt(e.target.value) || 5000 })}
              min={100}
              max={50000}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={field.required !== false}
                onChange={(e) => updateFieldConfig(fieldKey, { required: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-900"
              />
              <span className="text-sm text-gray-300">
                {t.admin?.inputTextRequired || 'Required'}
              </span>
            </label>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.EDIT} size={20} className="text-cyan-400" />
          {t.admin?.inputTextSettings || 'Input Text Settings'}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {t.admin?.inputTextSettingsDesc || 'Configure the title and placeholder text for the input text widget.'}
        </p>
      </section>

      {/* No Widget Warning */}
      {!hasInputTextWidget && (
        <section className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name={ICONS.INFO} size={20} className="text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-300 font-medium">
                {t.admin?.noInputTextWidget || 'Widget not enabled'}
              </p>
              <p className="text-sm text-yellow-400/80 mt-1">
                {t.admin?.noInputTextWidgetDesc || 'Enable "Input Text" widget in the Widgets section to configure its settings.'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Configuration Fields */}
      {hasInputTextWidget && (
        <>
          {/* Layout Mode Toggle */}
          <section className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-600/30 rounded-xl p-4">
            <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <Icon name={ICONS.SLIDERS} size={18} className="text-gray-400" />
              {t.admin?.inputTextLayout || 'Layout Mode'}
            </h4>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => updateConfig({ layout: 'simple' })}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  layout === 'simple'
                    ? 'bg-cyan-600 text-white border border-cyan-500'
                    : 'bg-gray-800 text-gray-400 border border-gray-600 hover:border-gray-500 hover:text-gray-300'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <Icon name={ICONS.PROMPTS} size={20} />
                  <span>{t.admin?.inputTextSimple || 'Simple'}</span>
                  <span className="text-xs opacity-70">{t.admin?.inputTextSimpleDesc || 'One text field'}</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => updateConfig({ layout: 'double' })}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  layout === 'double'
                    ? 'bg-cyan-600 text-white border border-cyan-500'
                    : 'bg-gray-800 text-gray-400 border border-gray-600 hover:border-gray-500 hover:text-gray-300'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <div className="flex gap-1">
                    <Icon name={ICONS.PROMPTS} size={16} />
                    <Icon name={ICONS.PROMPTS} size={16} />
                  </div>
                  <span>{t.admin?.inputTextDouble || 'Double'}</span>
                  <span className="text-xs opacity-70">{t.admin?.inputTextDoubleDesc || 'Two text fields'}</span>
                </div>
              </button>
            </div>
          </section>

          {/* Simple Mode Configuration */}
          {layout === 'simple' && (
            <>
              {/* Output Key */}
              <section className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-xl p-4">
                <h4 className="text-sm font-bold text-green-300 mb-3 flex items-center gap-2">
                  <Icon name={ICONS.TAG} size={18} className="text-green-400" />
                  {t.admin?.inputTextOutputKey || 'Output Key (API)'}
                </h4>
                <input
                  type="text"
                  value={config.outputKey || ''}
                  onChange={(e) => updateConfig({ outputKey: e.target.value || undefined })}
                  placeholder="prompt"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 placeholder-gray-500 font-mono"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {t.admin?.inputTextOutputKeyDesc || 'The key name used in the API payload (default: "prompt")'}
                </p>
              </section>

              {/* Widget Title */}
              <section className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-500/30 rounded-xl p-4">
                <h4 className="text-sm font-bold text-cyan-300 mb-3 flex items-center gap-2">
                  <Icon name={ICONS.TAG} size={18} className="text-cyan-400" />
                  {t.admin?.inputTextTitle || 'Widget Title'}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Russian Title */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <span className="text-base">🇷🇺</span>
                      Russian
                    </label>
                    <input
                      type="text"
                      value={config.titleRu || ''}
                      onChange={(e) => updateConfig({ titleRu: e.target.value || undefined })}
                      placeholder={t.inputText?.title || 'Ввод текста'}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
                    />
                  </div>

                  {/* English Title */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <span className="text-base">🇬🇧</span>
                      English
                    </label>
                    <input
                      type="text"
                      value={config.titleEn || ''}
                      onChange={(e) => updateConfig({ titleEn: e.target.value || undefined })}
                      placeholder="Input Text"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-gray-500"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  {language === 'ru'
                    ? 'Оставьте пустым для использования значения по умолчанию'
                    : 'Leave empty to use default value'}
                </p>
              </section>

              {/* Placeholder Text */}
              <section className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-4">
                <h4 className="text-sm font-bold text-purple-300 mb-3 flex items-center gap-2">
                  <Icon name={ICONS.PROMPTS} size={18} className="text-purple-400" />
                  {t.admin?.inputTextPlaceholder || 'Placeholder Text'}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Russian Placeholder */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <span className="text-base">🇷🇺</span>
                      Russian
                    </label>
                    <input
                      type="text"
                      value={config.placeholderRu || ''}
                      onChange={(e) => updateConfig({ placeholderRu: e.target.value || undefined })}
                      placeholder={t.inputText?.placeholder || 'Введите текст здесь...'}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 placeholder-gray-500"
                    />
                  </div>

                  {/* English Placeholder */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <span className="text-base">🇬🇧</span>
                      English
                    </label>
                    <input
                      type="text"
                      value={config.placeholderEn || ''}
                      onChange={(e) => updateConfig({ placeholderEn: e.target.value || undefined })}
                      placeholder="Enter your text here..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 placeholder-gray-500"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  {language === 'ru'
                    ? 'Текст-подсказка отображается в пустом поле ввода'
                    : 'Placeholder text is shown in the empty input field'}
                </p>
              </section>

              {/* Character Limit & Required Toggle */}
              <section className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 border border-gray-600/30 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <Icon name={ICONS.SLIDERS} size={14} className="text-gray-500" />
                      {t.admin?.inputTextCharLimit || 'Character Limit'}
                    </label>
                    <input
                      type="number"
                      value={config.charLimit ?? 5000}
                      onChange={(e) => updateConfig({ charLimit: parseInt(e.target.value) || 5000 })}
                      min={100}
                      max={50000}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.required !== false}
                        onChange={(e) => updateConfig({ required: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-900"
                      />
                      <span className="text-sm text-gray-300">
                        {t.admin?.inputTextRequired || 'Required'}
                      </span>
                    </label>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* Double Mode Configuration */}
          {layout === 'double' && (
            <>
              {/* Widget Title for Double Mode */}
              <section className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 border border-gray-600/30 rounded-xl p-4">
                <h4 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
                  <Icon name={ICONS.TAG} size={18} className="text-gray-400" />
                  {t.admin?.inputTextTitle || 'Widget Title'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <span className="text-base">🇷🇺</span> Russian
                    </label>
                    <input
                      type="text"
                      value={config.titleRu || ''}
                      onChange={(e) => updateConfig({ titleRu: e.target.value || undefined })}
                      placeholder={t.inputText?.title || 'Ввод текста'}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-gray-500 focus:ring-1 focus:ring-gray-500 placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                      <span className="text-base">🇬🇧</span> English
                    </label>
                    <input
                      type="text"
                      value={config.titleEn || ''}
                      onChange={(e) => updateConfig({ titleEn: e.target.value || undefined })}
                      placeholder="Input Text"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-gray-500 focus:ring-1 focus:ring-gray-500 placeholder-gray-500"
                    />
                  </div>
                </div>
              </section>

              {/* Field 1 Configuration */}
              {renderFieldConfig('field1', t.admin?.inputTextField1 || 'Field 1', 'from-cyan-900/20 to-blue-900/20')}

              {/* Field 2 Configuration */}
              {renderFieldConfig('field2', t.admin?.inputTextField2 || 'Field 2', 'from-purple-900/20 to-pink-900/20')}
            </>
          )}
        </>
      )}
    </div>
  );
}
